/* eslint-disable @typescript-eslint/no-explicit-any -- rows/columns here are untyped export data assembled from many different report sources; not touched by this change */
import { Injectable } from '@angular/core';
import { Workbook, Worksheet } from 'exceljs';
import * as fs from 'file-saver';
import moment from 'moment';
import { FieldType } from '../../enums/template';

@Injectable({ providedIn: 'root' })
export class ExcelReportService {
  async generateReport(
    logoBase64: string,
    reportTitle: string,
    columns: { header: string; key: string }[],
    data: any[],
    fileName = 'Report.xlsx'
  ) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Report');

    // --- 1. Insert Logo (small, top-left) ---
    if (logoBase64) {
      const imageId = workbook.addImage({
        base64: logoBase64,
        extension: 'png',
      });
      worksheet.addImage(imageId, {
        tl: { col: 0, row: 0 },
        ext: { width: 60, height: 40 },
      });
    }
    // --- 2. Report Title (centered across all columns) ---
    worksheet.mergeCells(1, 2, 2, columns.length);
    const titleCell = worksheet.getCell(1, 2);
    titleCell.value = reportTitle || 'Report';
    titleCell.font = { size: 18, bold: true };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(1).height = 30;

    // --- 3. Spacer Row ---
    worksheet.addRow([]);

    // --- 4. Column Headers ---
    const headerRow = worksheet.addRow(columns.map(c => c.header));
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '000000' },
    };
    headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
    headerRow.height = 20;

    // --- 5. Data Rows ---
   data.forEach(row => {
      const rowData: any[] = [];

      columns.forEach(c => {
        let cellValue = row[c.key];

        // Pre-check YYYY-MM-DD prefix to prevent bare 4-digit numbers (e.g. "2683") matching ISO year-only format
        if ( typeof cellValue === 'string' && /^\d{4}-\d{2}-\d{2}/.test(cellValue) && moment(cellValue, moment.ISO_8601, true).isValid() ) {
          const parsed = moment.utc(cellValue);
          cellValue = new Date(parsed.local().format('YYYY-MM-DD') + 'T00:00:00.000Z');
        }

        // 🧩 Detect "DD-MMM-YYYY" or "DD-MMM YYYY" (dash or space)
        else if (typeof cellValue === 'string' && /^\d{1,2}-?[A-Za-z]{3}[ -]\d{4}$/.test(cellValue.trim())) {
          const parsed = moment.utc(cellValue.trim(), ['DD-MMM-YYYY', 'DD-MMM YYYY']);
          if (parsed.isValid()) {
            cellValue = new Date(parsed.local().format('YYYY-MM-DD') + 'T00:00:00.000Z');
          }
        }

        // 🧩 Detect time-only fields like "09:52:57 am" or "16:37"
        else if (typeof cellValue === 'string' && /^\d{1,2}:\d{2}(:\d{2})?\s?(am|pm)?$/i.test(cellValue.trim())) {
          const parsed = moment.utc(cellValue.trim(), ['hh:mm:ss a', 'hh:mm a', 'HH:mm']);
          if (parsed.isValid()) {
            cellValue = parsed.local().toDate();
          }
        }

        else if (typeof cellValue === 'string' && this.isStrictIsoDateString(cellValue.trim())) {
          const parsed = moment.utc(cellValue.trim());
          if (parsed.isValid()) {
            cellValue = new Date(parsed.local().format('YYYY-MM-DD') + 'T00:00:00.000Z');
          }
        } else if (Array.isArray(cellValue)) {
          // Format tag/chip arrays into comma-separated text, or '-' if empty
          cellValue = cellValue.length > 0 && cellValue[0].name
            ? cellValue.map((tag: any) => tag.name).join(', ')
            : '-';
        }

        rowData.push(cellValue ?? '');
      });

      const addedRow = worksheet.addRow(rowData);
      addedRow.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    // ✅ Apply Excel date/time formats automatically
    // Date pattern is checked first — preferredEndTime/preferredStartTime contain "Time"
    // but represent calendar dates, not times of day.
    columns.forEach((c, idx) => {
      const col = worksheet.getColumn(idx + 1);
      if (/_time$/i.test(c.key)) {
        col.numFmt = 'hh:mm:ss AM/PM';
      } else if (/date|Date|workDate|preferredEndTime|preferredStartTime/i.test(c.key)) {
        col.numFmt = 'dd-mmm-yyyy';
      } else if (/(Time|Start|End)$/i.test(c.key)) {
        col.numFmt = 'hh:mm:ss AM/PM';
      }
    });

    // --- 6. Borders + Center Alignment for all cells ---
    worksheet.eachRow(row => {
      row.eachCell(cell => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      });
    });

    // --- 7. Smart Auto-fit Columns (Excel-like behavior with type safety) ---
    worksheet.columns.forEach(column => {
      let maxLength = 0;

      column.eachCell({ includeEmpty: true }, cell => {
        let cellValue = '';

        // Safely extract string value
        const value = cell.value;

        if (value != null) {
          if (typeof value === 'object' && 'richText' in value && Array.isArray(value.richText)) {
            // Handle rich text
            cellValue = value.richText.map(t => t.text).join('');
          } else if (value instanceof Date) {
            // Handle date cells
            cellValue = value.toLocaleDateString();
          } else {
            // Handle string / number / boolean / formula
            cellValue = value.toString();
          }
        }

        // Estimate column width similar to Excel's auto-fit
        const length = cellValue.length * 1.2;
        maxLength = Math.max(maxLength, length);
      });

      // Apply width with padding, ensuring min/max range
      const finalWidth = Math.min(Math.max(maxLength + 2, 12), 60);
      column.width = finalWidth;
    });
    // --- 8. Save file ---
    const buffer = await workbook.xlsx.writeBuffer();
    fs.saveAs(new Blob([buffer]), fileName);
  }

  isStrictIsoDateString(value: any): boolean {
    if (typeof value !== 'string') return false;

    const isoRegex =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;

    if (!isoRegex.test(value)) return false;

    return !isNaN(new Date(value).getTime());
  }

  // ---------------------------------------------------------------------------
  // Multi-sheet export: one sheet per unique value of groupByField
  // ---------------------------------------------------------------------------
  async generateMultiSheetReport(
    logoBase64: string,
    reportTitle: string,
    columns: { header: string; key: string; sectionCode: string }[],
    data: any[],
    fileName = 'Report.xlsx',
    groupByInfo: { key: string, caption: string }
  ) {
    const workbook = new Workbook();

    const getDynamicColumns = (row: any) => {
      const replaceChar = /\./gi;
      const columnList: { header: string; key: string; sectionCode: string }[] = columns.map(c => ({ ...c }));
      const sectionList: { caption: string; sectionCode: string; colCount: number, allowRepeat: boolean }[] = [];
      if(row.jsonData == undefined || row.jsonData == ''){
        return {columnList, sectionList};
      }

      if(typeof row.jsonData === 'string'){
        row.jsonData = JSON.parse(row.jsonData);
      }

      if (!row.jsonData || typeof row.jsonData !== 'object') {
        return {columnList, sectionList};
      }

      row.jsonData.forEach(page => {
        page.sections?.forEach(section => {
          if(section.sectionNo == undefined || section.sectionNo == 0){
            sectionList.push({ 
              caption: section.caption, 
              sectionCode: section.itemCode, 
              colCount: Math.max(section.items?.length ?? 1, 1),
              allowRepeat: section.allowRepeat
            });
            section.items?.forEach(item => {
              columnList.push({ 
                header: item.caption, 
                key: item.itemCode.replace(replaceChar, "") + (section.sectionNo??''),
                sectionCode: section.itemCode
              });
            });
          }
        });
      });
      return {columnList, sectionList};
    }

    // Group rows by the unique values of groupByField
    const groups = new Map<string, 
    { 
      items: any[]; 
      headers: { columnList: { header: string; key: string, sectionCode: string }[], sectionList: { caption: string; sectionCode: string; colCount: number }[] }, 
      sheetName: string 
    }>();

    for (const row of data) {
      const key = (row[groupByInfo.key] ?? 'Unknown').toString();
      if (!groups.has(key)) {
        groups.set(key, { items: [], headers: null as any, sheetName: row[groupByInfo.caption]});
      }
      groups.get(key)!.items.push(row);
    }

    // Discover dynamic columns using the first row in each group that has jsonData.
    // This ensures template field columns are picked up even if some rows lack jsonData.
    for (const [, groupData] of groups) {
      const representativeRow = groupData.items.find(r =>
        r.jsonData && typeof r.jsonData === 'object' && (Array.isArray(r.jsonData) ? r.jsonData.length > 0 : true)
      ) || groupData.items[0];
      groupData.headers = getDynamicColumns(representativeRow);
    }

    // Resolve a unique sheet name by checking already-added worksheets in the workbook.
    // This is more reliable than a manual Set because ExcelJS owns the truth about
    // existing names and comparisons are case-insensitive (matching Excel behaviour).
    const getUniqueSheetName = (baseName: string): string => {
      let candidate = baseName;
      let counter = 2;
      while (workbook.worksheets.some(ws => ws.name.toLowerCase() === candidate.toLowerCase())) {
        const suffix = ` (${counter++})`;
        candidate = this.sanitizeSheetName(
          (baseName ?? '').substring(0, 31 - suffix.length) + suffix
        );
      }
      return candidate;
    };

    for (const [, groupData] of groups) {
      const rawName = this.sanitizeSheetName(groupData.sheetName);
      const sheetName = getUniqueSheetName(rawName);
      const worksheet = workbook.addWorksheet(sheetName);
      this.fillWorksheet(workbook, worksheet, logoBase64, reportTitle, groupData.headers, groupData.items);
    }

    const buffer = await workbook.xlsx.writeBuffer();
    fs.saveAs(new Blob([buffer]), fileName);
  }

  private sanitizeSheetName(name: string): string {
    // Excel limits: max 31 chars, no / \ ? * [ ] :
    return name.replace(/[/\\?*[\]:]/g, '').substring(0, 31).trim() || 'Sheet';
  }

  private fillWorksheet(
    workbook: Workbook,
    worksheet: Worksheet,
    logoBase64: string,
    reportTitle: string,
    headers: {columnList : { header: string; key: string, sectionCode: string }[], sectionList: { caption: string; sectionCode: string, colCount: number }[] },
    data: any[]
  ) {
    const replaceChar = /\./gi;

    const totalCols = headers.columnList.length || 1;

    // --- 1. Logo ---
    if (logoBase64) {
      const imageId = workbook.addImage({ base64: logoBase64, extension: 'png' });
      worksheet.addImage(imageId, { tl: { col: 0, row: 0 }, ext: { width: 60, height: 40 } });
      if (totalCols > 1) {
        worksheet.mergeCells(1, 1, 2, 1);
      }
    }

    // --- 2. Report Title ---
    const titleStartCol = totalCols > 1 ? 2 : 1;
    if (titleStartCol <= totalCols) {
      worksheet.mergeCells(1, titleStartCol, 2, totalCols);
    }
    const titleCell = worksheet.getCell(1, titleStartCol);
    titleCell.value = data?.length > 0 ? data[0].title : (reportTitle || 'Report');
    titleCell.font = { size: 18, bold: true };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } };
    titleCell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 13 };
    worksheet.getRow(1).height = 30;

    // --- 3. Section Headers ---
    if(headers.sectionList?.length > 0){
      const sectionTitle = headers.sectionList.flatMap(item => [
        item.caption,
        ...Array(Math.max(item.colCount - 1, 0)).fill('')
      ]);

      const totalStaticCol = headers.columnList.filter(x => (x.sectionCode??'').length == 0).length;
      worksheet.addRow([
        ...Array(totalStaticCol).fill(''),
        ...sectionTitle
      ]);

      let currentSectionIndex = totalStaticCol;
      headers.sectionList.forEach((section: any) => {
        const startCol = currentSectionIndex + 1;
        const endCol = currentSectionIndex + section.colCount;
        if (startCol < endCol) {
          worksheet.mergeCells(3, startCol, 3, endCol);
        }
        const cell = worksheet.getCell(3, startCol);
        if(section.allowRepeat){
          cell.value = {
            richText: [
              {
                text: section.caption,
                font: { bold: true, color: { argb: 'FFFFFFFF' } }
              },
              {
                text: ' (repeated)',
                font: { size: 9, color: { argb: 'FFFFD966' }, bold: true }
              }
            ]
          };
        }

        // LEFT border → first cell
        const leftCell = worksheet.getCell(3, startCol);
        leftCell.border = {
          ...leftCell.border,
          left: { style: 'thin', color: { argb: 'FFF3F4F6' } }
        };

        // RIGHT border → last cell
        const rightCell = worksheet.getCell(3, endCol);
        rightCell.border = {
          ...rightCell.border,
          right: { style: 'thin', color: { argb: 'FFF3F4F6' } }
        };

        section.startIndex = startCol;
        currentSectionIndex += section.colCount;
      });
    }

    // --- 4. Column Headers ---
    const columnHeaderRow = worksheet.addRow(headers.columnList.map(c => c.header));
    columnHeaderRow.font = { bold: true };
    columnHeaderRow.alignment = { horizontal: 'left', vertical: 'middle' };
    columnHeaderRow.height = 20;

    // Apply style to section header and column header row
    for (let col = 1; col <= headers.columnList.length; col++) {
      const sectionCell = worksheet.getCell(3, col);
      sectionCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF172554' } };
      sectionCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      sectionCell.alignment = { horizontal: 'center', vertical: 'middle' };

      const columnHeaderCell = worksheet.getCell(4, col);
      columnHeaderCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
      columnHeaderCell.border = { 
        bottom: { style: 'medium', color: { argb: 'FFD1D5DB' } },
        right: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        left: { style: 'thin', color: { argb: 'FFD1D5DB' } } }
    }

    // --- 5. Data Rows ---
    const getFieldValue = (row: any, itemCode: string) => {
      let cellValue = row[itemCode];

        if (typeof cellValue === 'string') {
          const trimmed = cellValue.trim();
          if (/^\d{1,2}-?[A-Za-z]{3}[ -]\d{4}$/.test(trimmed)) {
            const parsed = moment.utc(trimmed, ['DD-MMM-YYYY', 'DD-MMM YYYY']);
            if (parsed.isValid()) cellValue = new Date(parsed.local().format('YYYY-MM-DD') + 'T00:00:00.000Z');
          } else if (/^\d{1,2}:\d{2}(:\d{2})?\s?(am|pm)?$/i.test(trimmed)) {
            const parsed = moment.utc(trimmed, ['hh:mm:ss a', 'hh:mm a', 'HH:mm']);
            if (parsed.isValid()) cellValue = parsed.local().toDate();
          } else if (/^\d{4}-\d{2}-\d{2}/.test(trimmed) && moment(trimmed, moment.ISO_8601, true).isValid()) {
            const parsed = moment.utc(trimmed);
            if (parsed.isValid()) cellValue = new Date(parsed.local().format('YYYY-MM-DD') + 'T00:00:00.000Z');
          } else if (this.isStrictIsoDateString(trimmed)) {
            const parsed = moment.utc(trimmed);
            if (parsed.isValid()) cellValue = new Date(parsed.local().format('YYYY-MM-DD') + 'T00:00:00.000Z');
          }
        } else if (Array.isArray(cellValue)) {
          // Tag/chip arrays by name, multi-select answers by value, '-' if nothing is left.
          // Answers can hold blanks (e.g. [null] or ["", ""]), which are skipped.
          const values = cellValue
            .map(v => (v && typeof v === 'object' ? v.name : v))
            .filter(v => v !== null && v !== undefined && String(v).trim() !== '');
          cellValue = values.length > 0 ? values.join(', ') : '-';
        }

        return cellValue ?? '';
    }

    data.forEach((row) => {
      const rowData: any[] = headers.columnList.map(c => {
        return getFieldValue(row, c.key);
      });

      // Repeated sections (sectionNo > 1 — a second/third/... filled instance of an
      // "allowRepeat" section) have no column of their own: only the base instance
      // (sectionNo 0/undefined) got one in getDynamicColumns(). This used to write each
      // repeat's values into a DIFFERENT row (4 + rowIndex + sectionNo), on the wrong
      // assumption that extra rows existed for them — every record occupies exactly one
      // row (added below), so that write landed on a LATER record's row, corrupting the
      // sheet (desynced row objects) whenever a template actually had repeat data filled
      // in. Instead, append each repeat's value into its matching base column's cell on
      // THIS row, so every write stays within the row being built.
      const jsonPages = Array.isArray(row.jsonData) ? row.jsonData : [];
      jsonPages.forEach(page => {
        page.sections?.filter(x => x.sectionNo > 1).forEach(section => {
          const sectionCols = headers.columnList
            .map((c, idx) => ({ c, idx }))
            .filter(({ c }) => c.sectionCode === section.itemCode);
          section.items?.forEach((item, itemIndex) => {
            if ([FieldType.Image, FieldType.Signature].includes(item.fieldType)) return;
            const target = sectionCols[itemIndex];
            if (!target) return;
            const formattedItemCode = item.itemCode.replace(replaceChar, "") + (section.sectionNo ?? '');
            const value = getFieldValue(row, formattedItemCode);
            if (value === '' || value == null) return;
            rowData[target.idx] = rowData[target.idx] ? `${rowData[target.idx]} | ${value}` : value;
          });
        });
      });

      const addedRow = worksheet.addRow(rowData);
      addedRow.alignment = { horizontal: 'left', vertical: 'middle' };
    });

    // --- 6. Date/time column formats ---
    // Date pattern checked first — preferredEndTime/preferredStartTime contain "Time"
    // but represent calendar dates, not times of day.
    headers.columnList.forEach((c, idx) => {
      const col = worksheet.getColumn(idx + 1);
      if (/_time$/i.test(c.key)) col.numFmt = 'hh:mm:ss AM/PM';
      else if (/date|Date|workDate|preferredEndTime|preferredStartTime/i.test(c.key)) col.numFmt = 'dd-mmm-yyyy';
      else if (/(Time|Start|End)$/i.test(c.key)) col.numFmt = 'hh:mm:ss AM/PM';
    });

    // --- 7. Borders ---
    worksheet.eachRow((row, index) => {
      if(index > 4){
        row.eachCell(cell => {
          cell.border = {
            ...cell.border,
            bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } }
          };
          //cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        });
      }
    });

    // --- 8. Auto-fit columns ---
    worksheet.columns.forEach(column => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, cell => {
        const value = cell.value;
        let str = '';
        if (value != null) {
          if (typeof value === 'object' && 'richText' in value && Array.isArray(value.richText)) {
            str = value.richText.map(t => t.text).join('');
          } else if (value instanceof Date) {
            str = value.toLocaleDateString();
          } else {
            str = value.toString();
          }
        }
        maxLength = Math.max(maxLength, str.length * 1.2);
      });
      column.width = Math.min(Math.max(maxLength + 2, 12), 60);
    });

    // Freeze panes (rows 0-2 frozen)
    worksheet.views = [{ state: 'frozen', ySplit: 4 }]
  }
}
